import { Box, Button, Flex, SimpleGrid, Text } from '@chakra-ui/react'
import { useState } from 'react'
import { PLINKO_CONTRACT_ADDRESS } from '@/constants/constants'
import { useAudio, SOUNDS } from '@/context/AudioProvider'
import posthog from 'posthog-js'
import { TokenMoney } from '@ninjablaze/sdk-ts'
import { useMutation } from '@tanstack/react-query'
import { DoubleColor } from '@/__generated__/graphql'
import { placeBetTransaction } from '@/services/GameBroadcaster'
import BallHighIcon from '@/public/assets/images/plinko/ball_high_1.png'
import BallMediumIcon from '@/public/assets/images/plinko/ball_medium_1.png'
import BallLowIcon from '@/public/assets/images/plinko/ball_low_1.png'
import Image from 'next/image'
import { StaticImport } from 'next/dist/shared/lib/get-img-props'
import { getPlinkoGameResult } from '@/services/api-game'
import { usePlinkoStore } from '@/context/PlinkoProvider'
import { tryCatchAsync } from '@/helpers/trycatch'
import { useTranslation } from 'next-i18next/pages'
import { create } from 'zustand'

interface PlinkoAmountStore {
  amounts: Record<DoubleColor, number>
  multipliers: Record<DoubleColor, number>
  selectedColor: DoubleColor
  setAmount: (color: DoubleColor, value: number) => void
  toggleMultiplier: (color: DoubleColor) => void
  setSelectedColor: (color: DoubleColor) => void
}

export const usePlinkoAmountStore = create<PlinkoAmountStore>((set) => ({
  amounts: {
    [DoubleColor.Green]: 1,
    [DoubleColor.Black]: 1,
    [DoubleColor.Red]: 1,
  },
  multipliers: {
    [DoubleColor.Green]: 1,
    [DoubleColor.Black]: 1,
    [DoubleColor.Red]: 1,
  },
  selectedColor: DoubleColor.Green,
  setAmount: (color, value) =>
    set((state) => ({
      amounts: { ...state.amounts, [color]: value },
    })),
  toggleMultiplier: (color) =>
    set((state) => ({
      multipliers: {
        ...state.multipliers,
        [color]: state.multipliers[color] === 1 ? 10 : 1,
      },
    })),
  setSelectedColor: (color) => set({ selectedColor: color }),
}))

export const PlinkoBets = () => {
  const colors = [DoubleColor.Green, DoubleColor.Black, DoubleColor.Red]

  return (
    <>
      <SimpleGrid
        columns={{ base: 1, md: 3 }}
        gap={2}
        mb={{ base: 4, md: 9 }}
        alignItems={'self-start'}
        display={{ base: 'none', md: 'grid' }}
      >
        {colors.map((color) => (
          <Box
            key={color}
            order={{ base: color === DoubleColor.Black ? 2 : 1, md: 1 }}
            gridColumnStart={color === DoubleColor.Black ? { base: 1, md: 2 } : 'auto'}
            gridColumnEnd={color === DoubleColor.Black ? { base: 3, md: 2 } : 'auto'}
          >
            <PlinkoBetsColumn type={color} />
          </Box>
        ))}
      </SimpleGrid>
    </>
  )
}

const PlinkoControlButton = ({
  value,
  disabled,
  isActive,
  onClick,
  bgColor,
  bgImg,
  displayValue,
  ...props
}: {
  value: string
  disabled?: boolean
  isActive?: boolean
  onClick: () => void
  bgColor: string
  bgImg: StaticImport
  displayValue?: string
}) => {
  const { t } = useTranslation('common')
  const actualValue = displayValue || value
  return (
    <Box
      flex={1}
      rounded={'xl'}
      bg={{ base: 'ninja.600', sm: 'ninja.500' }}
      pos={'relative'}
      overflow={'hidden'}
      {...props}
    >
      <Button
        isDisabled={disabled}
        onClick={() => onClick()}
        h={9}
        pos={'relative'}
        zIndex={10}
        variant={'secondary'}
        transition={'0.2s'}
        w={'full'}
        bg={'transparent'}
        boxShadow={isActive ? `0px 0px 12px 0px ${bgColor} inset!important` : 'none'}
        color={isActive ? 'white' : 'ninja.300'}
        overflow={'hidden'}
        textOverflow={'ellipsis'}
        whiteSpace={'nowrap'}
        textAlign={'center'}
        justifyContent={'center'}
        sx={{
          '+ div': {
            transform: isActive ? 'rotate(45deg) translate(-32px, 0)' : 'none',
          },
        }}
        _hover={{
          boxShadow: `0px 0px 12px 0px ${bgColor} inset`,
          color: 'white',
          '+ div': {
            transform: 'rotate(45deg) translate(-32px, 0)',
          },
        }}
        border={'none'}
        size={'xs'}
      >
        {actualValue} {actualValue === '1' ? t('plinko.ball') : t('plinko.balls')}
      </Button>
      <Box transition={'0.2s'} pos={'absolute'} width={'40px'} height={'40px'} top={'90%'} left={'95%'}>
        <Image src={bgImg} alt={''} />
      </Box>
    </Box>
  )
}

const PlinkoBetsColumn = ({ type }: { type: DoubleColor }) => {
  return (
    <Box bg={{ base: 'none', md: 'ninja.600' }} rounded={'3xl'} p={{ base: 0, md: 3 }}>
      <BetsButton type={type} />
    </Box>
  )
}

const BetsButton = ({ type }: { type: DoubleColor }) => {
  const { t } = useTranslation('common')
  const amount = usePlinkoStore((state) => state.amount)
  const denom = usePlinkoStore((state) => state.denom)

  const [isLoading, setIsLoading] = useState(false)
  const audio = useAudio()

  const { mutateAsync: handleBet } = useMutation({
    mutationFn: async (color: DoubleColor) => {
      const map = () => {
        switch (color) {
          case DoubleColor.Red:
            return 'high'
          case DoubleColor.Black:
            return 'medium'
          case DoubleColor.Green:
            return 'low'
          default:
            return ''
        }
      }
      setIsLoading(true)

      const amountValue = usePlinkoAmountStore.getState().amounts[color]
      const multiplier = usePlinkoAmountStore.getState().multipliers[color]
      const actualBallsCount = amountValue * multiplier

      const tx = await tryCatchAsync(
        placeBetTransaction({
          contract_addr: PLINKO_CONTRACT_ADDRESS,
          funds: {
            denom: denom,
            amount: (Number(amount) * actualBallsCount).toString(),
          },
          msg: {
            risk: map(),
            balls: actualBallsCount,
            bet: TokenMoney.fromHuman(amount.toString(), denom).toCoin(),
          },
        })
      )
      setIsLoading(false)
      if (tx.error) {
        console.log('placeBetTransaction error', tx.error)
        audio.play(SOUNDS.PLAYER_BET_ERROR)
        posthog.capture('user_placed_bet_error', { game: 'plinko' })
        return
      }

      const event = tx!.data!.events?.find((e) => e.type === 'wasm-plinko_v1_game_created')
      const gameId = event?.attributes?.find((a: any) => a.key === 'game_id')?.value
      const random_round = event?.attributes?.find((a: any) => a.key === 'random_round')?.value
      const game_result = event?.attributes?.find((a: any) => a.key === 'internal_game_result')?.value

      const gameTx = {
        gameId,
        random_round,
        game_result,
      }

      const getResult =
        gameTx.game_result ||
        ((await getPlinkoGameResult({
          gameId: gameTx!.gameId,
          randomRound: gameTx!.random_round,
          balls: actualBallsCount,
          rows: 16,
        })) as number[])

      posthog.capture('user_placed_bet', { game: 'plinko' })
      ;(document.getElementById('game')! as any).contentWindow!.postMessage(
        {
          type: 'dropBalls',

          value: getResult.map((x: number) => ({
            id: 0,
            result: (x - 8) * -1,
            riskLevel: map(),
          })),
        },
        '*'
      )
    },
  })

  const amountValue = usePlinkoAmountStore((state) => state.amounts[type])
  const multiplier = usePlinkoAmountStore((state) => state.multipliers[type])

  const handleSetAmount = (value: number) => {
    usePlinkoAmountStore.getState().setAmount(type, value)
  }

  const bgColor = () => {
    switch (type) {
      case DoubleColor.Green:
        return '#30C6AC'
      case DoubleColor.Black:
        return '#7C71F6'
      case DoubleColor.Red:
        return '#C077F7'
      default:
        return '#30C6AC'
    }
  }
  const bgImg = () => {
    switch (type) {
      case DoubleColor.Green:
        return BallLowIcon
      case DoubleColor.Black:
        return BallMediumIcon
      case DoubleColor.Red:
        return BallHighIcon
      default:
        return BallLowIcon
    }
  }
  return (
    <Flex flexDir={'column'} gap={3} mb={{ base: 4, md: 0 }}>
      <Flex
        flexDir={'row'}
        justifyContent={'stretch'}
        gap={3}
        mb={{ base: 4, md: 0 }}
        display={{ base: 'none', md: 'flex' }}
      >
        <PlinkoControlButton
          bgColor={bgColor()}
          bgImg={bgImg()}
          value="1"
          displayValue={(1 * multiplier).toString()}
          isActive={amountValue == 1}
          disabled={false}
          onClick={() => handleSetAmount(1)}
        />
        <PlinkoControlButton
          bgColor={bgColor()}
          bgImg={bgImg()}
          value="5"
          displayValue={(5 * multiplier).toString()}
          isActive={amountValue == 5}
          disabled={false}
          onClick={() => handleSetAmount(5)}
        />
        <PlinkoControlButton
          bgColor={bgColor()}
          bgImg={bgImg()}
          value="10"
          displayValue={(10 * multiplier).toString()}
          isActive={amountValue == 10}
          disabled={false}
          onClick={() => handleSetAmount(10)}
        />
        <Button
          onClick={() => usePlinkoAmountStore.getState().toggleMultiplier(type)}
          h={9}
          w={'35px'}
          minW={'35px'}
          variant={'secondary'}
          bg={multiplier === 10 ? 'ninja.700' : 'ninja.600'}
          color={multiplier === 10 ? 'white' : 'ninja.300'}
          _hover={{
            bg: 'ninja.700',
            color: 'white',
          }}
          border={'none'}
          size={'xs'}
          fontSize={'9px'}
          fontWeight={'bold'}
          px={0}
        >
          x10
        </Button>
      </Flex>
      <Flex gap={1} mb={{ base: 0, md: 3 }}>
        <Button
          onClick={() => handleBet(type)}
          isLoading={isLoading}
          bgGradient={buttonsColors[type]}
          _hover={{
            bgGradient: buttonsHoverColors[type],
            boxShadow: buttonsHoverShadow[type],
          }}
          _active={{
            bgGradient: buttonsHoverColors[type],
          }}
          border={'none'}
          w={'full'}
          h={{ base: 12, md: 16 }}
          rounded={'2xl'}
          px={{ base: '4', xl: '6' }}
        >
          <Flex
            w={'full'}
            flexDir={{ base: 'column', sm: 'row', md: 'column', xl: 'row' }}
            justify={'center'}
            alignItems={{ base: 'start', sm: 'center', md: 'start', xl: 'center' }}
          >
            <Text>{t('common.place-a-bet')}</Text>
          </Flex>
        </Button>
      </Flex>
    </Flex>
  )
}

const buttonsColors: Record<DoubleColor, string> = {
  [DoubleColor.Black]: `linear-gradient(292deg, #8E83FB 31.11%, #5D52EE 97.01%);`,
  [DoubleColor.Red]: `linear-gradient(99deg, #E383FB -4.78%, #5D52EE 102.77%);`,
  [DoubleColor.Green]: `linear-gradient(99deg, #73DE31 -54.11%, #0BB9F0 101.79%), linear-gradient(93deg, #24C4DA 4.34%, #5F95FF 99.76%);`,
}

const buttonsHoverColors: Record<DoubleColor, string> = {
  [DoubleColor.Black]: `linear-gradient(#645AD6, #645AD6);`,
  [DoubleColor.Red]: `linear-gradient(#A26BF5, #A26BF5)`,
  [DoubleColor.Green]: `linear-gradient(#30B28F, #30B28F);`,
}

const buttonsHoverShadow: Record<DoubleColor, string> = {
  [DoubleColor.Black]: `0px 2px 48px -4px #645AD6CC;`,
  [DoubleColor.Red]: `0px 2px 48px -4px #645AD6CC;`,
  [DoubleColor.Green]: `0px 2px 48px -4px #30B28FCC;`,
}

export const PlinkoBallsSelector = () => {
  const { t } = useTranslation('common')
  const selectedColor = usePlinkoAmountStore((state) => state.selectedColor)
  const amountValue = usePlinkoAmountStore((state) => state.amounts[selectedColor])
  const multiplier = usePlinkoAmountStore((state) => state.multipliers[selectedColor])
  const setSelectedColor = usePlinkoAmountStore((state) => state.setSelectedColor)
  const amount = usePlinkoStore((state) => state.amount)
  const denom = usePlinkoStore((state) => state.denom)
  const [isLoading, setIsLoading] = useState<Record<string, boolean>>({})
  const audio = useAudio()

  const { mutateAsync: handleBet } = useMutation({
    mutationFn: async (color: DoubleColor) => {
      const map = () => {
        switch (color) {
          case DoubleColor.Red:
            return 'high'
          case DoubleColor.Black:
            return 'medium'
          case DoubleColor.Green:
            return 'low'
          default:
            return ''
        }
      }
      setIsLoading((prev) => ({ ...prev, [color]: true }))

      const amountValue = usePlinkoAmountStore.getState().amounts[color]
      const multiplier = usePlinkoAmountStore.getState().multipliers[color]
      const actualBallsCount = amountValue * multiplier

      const tx = await tryCatchAsync(
        placeBetTransaction({
          contract_addr: PLINKO_CONTRACT_ADDRESS,
          funds: {
            denom: denom,
            amount: (Number(amount) * actualBallsCount).toString(),
          },
          msg: {
            risk: map(),
            balls: actualBallsCount,
            bet: TokenMoney.fromHuman(amount.toString(), denom).toCoin(),
          },
        })
      )
      setIsLoading((prev) => ({ ...prev, [color]: false }))
      if (tx.error) {
        console.log('placeBetTransaction error', tx.error)
        audio.play(SOUNDS.PLAYER_BET_ERROR)
        posthog.capture('user_placed_bet_error', { game: 'plinko' })
        return
      }

      const event = tx!.data!.events?.find((e) => e.type === 'wasm-plinko_v1_game_created')
      const gameId = event?.attributes?.find((a: any) => a.key === 'game_id')?.value
      const random_round = event?.attributes?.find((a: any) => a.key === 'random_round')?.value
      const game_result = event?.attributes?.find((a: any) => a.key === 'internal_game_result')?.value

      const gameTx = {
        gameId,
        random_round,
        game_result,
      }

      const getResult =
        gameTx.game_result ||
        ((await getPlinkoGameResult({
          gameId: gameTx!.gameId,
          randomRound: gameTx!.random_round,
          balls: actualBallsCount,
          rows: 16,
        })) as number[])

      posthog.capture('user_placed_bet', { game: 'plinko' })
      ;(document.getElementById('game')! as any).contentWindow!.postMessage(
        {
          type: 'dropBalls',

          value: getResult.map((x: number) => ({
            id: 0,
            result: (x - 8) * -1,
            riskLevel: map(),
          })),
        },
        '*'
      )
    },
  })

  const colors = [DoubleColor.Green, DoubleColor.Black, DoubleColor.Red]

  return (
    <Box display={{ base: 'block', md: 'none' }} rounded={'20px'} mb={4} bg={'ninja.600'} p={4}>
      <Box display={'grid'} gridTemplateColumns={'1fr 1fr 1fr'} gap={2} mb={3}>
        {colors.map((color) => (
          <Button
            key={color}
            onClick={() => {
              setSelectedColor(color)
              handleBet(color)
            }}
            isLoading={isLoading[color]}
            bgGradient={buttonsColors[color]}
            _hover={{
              bgGradient: buttonsHoverColors[color],
              boxShadow: buttonsHoverShadow[color],
            }}
            _active={{
              bgGradient: buttonsHoverColors[color],
            }}
            border={'none'}
            h={12}
            rounded={'2xl'}
            fontSize={'sm'}
          >
            {t('common.place-a-bet')}
          </Button>
        ))}
      </Box>
      <Box display={'grid'} gridTemplateColumns={'1fr 1fr 1fr'} gap={2}>
        <PlinkoControlButton
          bgColor={'#30C6AC'}
          bgImg={BallLowIcon}
          value="1"
          displayValue={(1 * multiplier).toString()}
          isActive={amountValue === 1}
          disabled={false}
          onClick={() => usePlinkoAmountStore.getState().setAmount(selectedColor, 1)}
        />
        <PlinkoControlButton
          bgColor={'#7C71F6'}
          bgImg={BallMediumIcon}
          value="5"
          displayValue={(5 * multiplier).toString()}
          isActive={amountValue === 5}
          disabled={false}
          onClick={() => usePlinkoAmountStore.getState().setAmount(selectedColor, 5)}
        />
        <Flex>
          <PlinkoControlButton
            bgColor={'#C077F7'}
            bgImg={BallHighIcon}
            value="10"
            displayValue={(10 * multiplier).toString()}
            isActive={amountValue === 10}
            disabled={false}
            onClick={() => usePlinkoAmountStore.getState().setAmount(selectedColor, 10)}
          />
          <Button
            onClick={() => usePlinkoAmountStore.getState().toggleMultiplier(selectedColor)}
            h={9}
            ml={2}
            w={'35px'}
            minW={'35px'}
            variant={'secondary'}
            bg={multiplier === 10 ? 'ninja.700' : 'ninja.500'}
            color={multiplier === 10 ? 'white' : 'ninja.300'}
            _hover={{
              bg: 'ninja.700',
              color: 'white',
            }}
            border={'none'}
            size={'xs'}
            fontSize={'9px'}
            fontWeight={'bold'}
            px={0}
          >
            x10
          </Button>
        </Flex>
      </Box>
    </Box>
  )
}
